import { useEffect, useId, useState } from "react";
import { useLocation } from "react-router-dom";
import type { EnvironmentRole, UserPreferences, UserPreferencesUpdate } from "@starsach/shared-types";
import { useLeaveOrganisation, useMyMemberships } from "../api/hooks";
import { useStepUp } from "../auth/StepUpProvider";
import { useAuth } from "../auth/AuthProvider";
import { Card, DataTable, EmptyState, ErrorState, LoadingState, PageHeader, RelativeTime, StatusPill, btnSecondary, inputClass, sentence } from "../components/ui";
import { detectedTimeZone, formatWhen, landingPages } from "../lib/preferences";
import { SecuritySection } from "./account/SecuritySection";

/** What each role may do, in the words the Access tab already uses (section 13.4: one catalogue, shown to the holder). */
export const ROLE_SUMMARY: Record<EnvironmentRole, string> = {
  admin: "Authors configuration and sets overrides. Changes that need approval wait for a manager.",
  manager: "Everything an admin can, and approves changes and defines the promotion chain.",
};

const THEMES: Array<{ value: UserPreferences["theme"]; label: string }> = [
  { value: "system", label: "Follow the device" },
  { value: "light", label: "Light" },
  { value: "dark", label: "Dark" },
];
const DATE_FORMATS: Array<{ value: UserPreferences["dateFormat"]; label: string }> = [
  { value: "auto", label: "Device default" },
  { value: "iso", label: "2026-09-14 18:05 (ISO 8601)" },
  { value: "us", label: "09/14/2026, 6:05 PM (US)" },
  { value: "intl", label: "14/09/2026 18:05 (international)" },
];
const MOTIONS: Array<{ value: UserPreferences["motion"]; label: string }> = [
  { value: "auto", label: "Follow the device" },
  { value: "standard", label: "Standard" },
  { value: "reduced", label: "Reduced" },
];

/** Requirements section 13: one page, sections in the order Profile, Preferences, Security, Access, Privacy - Security and Privacy arrive with their own issues. */
export function AccountPage() {
  const { me, preferences, updatePreferences } = useAuth();
  const { hash } = useLocation();
  useEffect(() => {
    if (hash) document.getElementById(hash.slice(1))?.scrollIntoView();
  }, [hash]);
  const [saving, setSaving] = useState<keyof UserPreferences>();
  const [saved, setSaved] = useState<keyof UserPreferences>();
  const [error, setError] = useState<unknown>();
  const zones = timeZones();
  const detected = detectedTimeZone();
  const ids = { theme: useId(), zone: useId(), format: useId(), landing: useId(), motion: useId() };

  const save = async (patch: UserPreferencesUpdate) => {
    const key = Object.keys(patch)[0] as keyof UserPreferences;
    setSaving(key);
    setSaved(undefined);
    setError(undefined);
    try {
      await updatePreferences(patch);
      setSaved(key);
    } catch (e) {
      setError(e);
    } finally {
      setSaving(undefined);
    }
  };
  const status = (key: keyof UserPreferences) => (saving === key ? <span className="ml-2 text-xs text-neutral-500">Saving…</span> : saved === key ? <span role="status" className="ml-2 text-xs text-green-700 dark:text-green-300">Saved</span> : null);
  const now = new Date().toISOString();

  return (
    <>
      <PageHeader title="Account" subtitle={me ? `${me.email ?? me.subject} · ${me.viewAs ? "Gairm staff" : sentence(me.role)}` : undefined} />
      {me?.viewAs && (
        <p role="status" className="mb-4 rounded border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-900 dark:bg-amber-950 dark:text-amber-100">
          These are your own preferences, not the customer's, and a view-as tab is read-only. Change them from your own tab.
        </p>
      )}
      <div className="space-y-6">
        <ProfileSection />
        <Card title="Preferences">
          <div className="grid gap-5 md:grid-cols-2">
            <label htmlFor={ids.theme} className="text-sm">
              <span className="block font-medium">Theme</span>
              <select id={ids.theme} className={inputClass} value={preferences.theme} disabled={Boolean(me?.viewAs)} onChange={(e) => void save({ theme: e.target.value as UserPreferences["theme"] })}>
                {THEMES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
              {status("theme")}
            </label>

            <label htmlFor={ids.motion} className="text-sm">
              <span className="block font-medium">Motion</span>
              <select id={ids.motion} className={inputClass} value={preferences.motion} disabled={Boolean(me?.viewAs)} onChange={(e) => void save({ motion: e.target.value as UserPreferences["motion"] })}>
                {MOTIONS.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
              </select>
              <span className="mt-1 block text-xs text-neutral-500">Reduced turns off transitions that only decorate; spinners and progress keep moving.</span>
              {status("motion")}
            </label>

            <label htmlFor={ids.zone} className="text-sm">
              <span className="block font-medium">Time zone</span>
              <select id={ids.zone} className={inputClass} value={preferences.timeZone} disabled={Boolean(me?.viewAs)} onChange={(e) => void save({ timeZone: e.target.value })}>
                <option value="auto">Detect from this device ({detected})</option>
                {zones.map((z) => <option key={z} value={z}>{z.replace(/_/g, " ")}</option>)}
              </select>
              <span className="mt-1 block text-xs text-neutral-500">Everything is stored in UTC and shown in this zone: alarms, audit, journeys, quotas.</span>
              {status("timeZone")}
            </label>

            <label htmlFor={ids.format} className="text-sm">
              <span className="block font-medium">Date format</span>
              <select id={ids.format} className={inputClass} value={preferences.dateFormat} disabled={Boolean(me?.viewAs)} onChange={(e) => void save({ dateFormat: e.target.value as UserPreferences["dateFormat"] })}>
                {DATE_FORMATS.map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
              </select>
              <span className="mt-1 block text-xs text-neutral-500">Right now reads as <time dateTime={now}>{formatWhen(now, preferences)}</time>.</span>
              {status("dateFormat")}
            </label>

            <label htmlFor={ids.landing} className="text-sm">
              <span className="block font-medium">Start on</span>
              <select id={ids.landing} className={inputClass} value={preferences.landingPage} disabled={Boolean(me?.viewAs)} onChange={(e) => void save({ landingPage: e.target.value })}>
                {landingPages(me?.role).map((l) => <option key={l.path} value={l.path}>{l.label}</option>)}
              </select>
              <span className="mt-1 block text-xs text-neutral-500">Where each sign-in takes you.</span>
              {status("landingPage")}
            </label>
          </div>
          {error !== undefined && <ErrorState error={error} />}
        </Card>
        <SecuritySection />
        <AccessSection />
      </div>
    </>
  );
}

/** Section 13.2: first and last name, from which the display name is made. The email remains who you are to the audit log. */
function ProfileSection() {
  const { me, updateProfile } = useAuth();
  const [first, setFirst] = useState(me?.firstName ?? "");
  const [last, setLast] = useState(me?.lastName ?? "");
  const [state, setState] = useState<"idle" | "saving" | "saved">("idle");
  const [error, setError] = useState<unknown>();
  const id = useId();
  const dirty = first.trim() !== (me?.firstName ?? "") || last.trim() !== (me?.lastName ?? "");
  if (me?.identityProvider) {
    return (
      <Card title="Profile">
        <p className="text-sm"><span className="font-medium">Name:</span> {me.displayName ?? "not provided"}</p>
        <p className="mt-1 text-xs text-neutral-500">Your name comes from {me.identityProvider} and is updated each time you sign in. Change it there, then sign in again. The audit trail keeps your email ({me.email ?? me.subject}) as who acted.</p>
      </Card>
    );
  }
  return (
    <Card title="Profile">
      <form
        className="flex flex-wrap items-end gap-3"
        onSubmit={(e) => {
          e.preventDefault();
          setState("saving");
          setError(undefined);
          updateProfile(first, last)
            .then(() => setState("saved"))
            .catch((err) => {
              setError(err);
              setState("idle");
            });
        }}
      >
        <label htmlFor={`${id}-first`} className="text-sm">
          <span className="block font-medium">First name</span>
          <input id={`${id}-first`} className={`${inputClass} w-44`} value={first} maxLength={40} autoComplete="given-name" disabled={Boolean(me?.viewAs)} onChange={(e) => { setFirst(e.target.value); setState("idle"); }} />
        </label>
        <label htmlFor={`${id}-last`} className="text-sm">
          <span className="block font-medium">Last name</span>
          <input id={`${id}-last`} className={`${inputClass} w-44`} value={last} maxLength={40} autoComplete="family-name" disabled={Boolean(me?.viewAs)} onChange={(e) => { setLast(e.target.value); setState("idle"); }} />
        </label>
        <button type="submit" className={btnSecondary} disabled={!dirty || state === "saving" || Boolean(me?.viewAs)}>
          {state === "saving" ? "Saving…" : "Save name"}
        </button>
        {state === "saved" && <span role="status" className="text-xs text-green-700 dark:text-green-300">Saved</span>}
        <span className="basis-full text-xs text-neutral-500">How the portal addresses you{me?.displayName ? ` - shown as "${me.displayName}"` : ""}. The audit trail keeps your email ({me?.email ?? me?.subject}) as who acted.</span>
      </form>
      {error !== undefined && <ErrorState error={error} />}
    </Card>
  );
}

/**
 * Section 13.4 [30] - where do I have access, and as what. A role is held
 * per environment and never rides the token, so without this a user has
 * no way to see their own access at all. Read-only: every grant and
 * revoke still goes through the environment's Access tab.
 */
function AccessSection() {
  const { me, signOut } = useAuth();
  const q = useMyMemberships();
  const leave = useLeaveOrganisation();
  const { ensureFresh } = useStepUp();
  const [confirmLeave, setConfirmLeave] = useState(false);
  return (
    <div id="access" className="scroll-mt-6 space-y-4">
      <Card title="Access">
        <dl className="grid grid-cols-[9rem_1fr] gap-x-6 gap-y-2 text-sm">
          <dt className="text-neutral-600 dark:text-neutral-400">Signed in as</dt>
          <dd className="text-neutral-900 dark:text-neutral-100">{me?.email ?? me?.subject ?? "—"}</dd>
          <dt className="text-neutral-600 dark:text-neutral-400">Organization</dt>
          <dd className={me?.organisationId ? "font-mono text-neutral-900 dark:text-neutral-100" : "text-neutral-900 dark:text-neutral-100"}>{me?.organisationId ?? (me?.role === "internal-team" ? "Gairm (internal team)" : "—")}</dd>
          <dt className="text-neutral-600 dark:text-neutral-400">Access tier</dt>
          <dd className="text-neutral-900 dark:text-neutral-100">{me?.role ? sentence(me.role) : "—"}</dd>
        </dl>
        <p className="mt-3 text-xs text-neutral-500">A role is held per environment, so authority in one says nothing about anywhere else. Changes are made on an environment's Access tab by a manager.</p>
        {me?.role === "customer" && !me.viewAs && (
          <div className="mt-4 border-t border-neutral-200 pt-4 dark:border-neutral-800">
            {!confirmLeave ? (
              <button type="button" className={btnSecondary} onClick={() => setConfirmLeave(true)}>Leave this organization</button>
            ) : (
              <div className="flex flex-wrap items-center gap-3">
                <span className="text-sm">Your roles are revoked and your sign-in disabled. A manager can reinstate you within thirty days.</span>
                <button type="button" className={btnSecondary} disabled={leave.isPending} onClick={() => void ensureFresh().then(() => leave.mutate(undefined, { onSuccess: () => void signOut() }), () => undefined)}>
                  {leave.isPending ? "Leaving…" : "Yes, leave"}
                </button>
                <button type="button" className={btnSecondary} onClick={() => setConfirmLeave(false)}>Cancel</button>
              </div>
            )}
            {leave.isError && <ErrorState error={leave.error} />}
          </div>
        )}
      </Card>
      {q.isPending && <LoadingState label="Loading your access" />}
      {q.isError && <ErrorState error={q.error} onRetry={() => void q.refetch()} />}
      {q.data?.scope === "internal-team" && (
        <Card title="Where you have access">
          <p className="text-sm text-neutral-700 dark:text-neutral-300">
            You are on the internal team, so you can see every environment in every organization. That access comes from your group, not from per-environment membership, so nothing is listed here.
          </p>
        </Card>
      )}
      {q.data?.scope === "membership" && (
        <Card title="Where you have access">
          <DataTable
            caption="Environments you hold a role on, and the role you hold"
            rowKey={(m) => m.environmentId}
            sortBy={(m) => m.friendlyName ?? m.environmentId}
            rows={q.data.memberships}
            empty={<EmptyState title="You do not hold a role on any environment yet" hint="A manager on an environment can grant you one from its Access tab." />}
            columns={[
              {
                key: "environment",
                header: "Environment",
                render: (m) => (
                  <div>
                    <div className="text-neutral-900 dark:text-neutral-100">{m.friendlyName ?? m.environmentId}</div>
                    <div className="font-mono text-xs text-neutral-600 dark:text-neutral-400">{m.awsAccountNumber ?? m.environmentId}</div>
                  </div>
                ),
              },
              { key: "role", header: "Your role", render: (m) => <StatusPill status={m.role} tone={m.role === "manager" ? "ok" : "neutral"} /> },
              { key: "means", header: "What that means", render: (m) => <span className="text-sm">{ROLE_SUMMARY[m.role]}</span> },
              {
                key: "granted",
                header: "Granted",
                render: (m) => (m.grantedAt ? <span className="text-sm"><RelativeTime iso={m.grantedAt} />{m.grantedBy && <span className="block text-xs text-neutral-500">by {m.grantedBy}</span>}</span> : <span className="text-neutral-500">—</span>),
              },
            ]}
          />
        </Card>
      )}
    </div>
  );
}

/** Every IANA zone the browser knows, with the common ones the same as anywhere else; a short list when the API is missing. */
function timeZones(): string[] {
  const intl = Intl as unknown as { supportedValuesOf?: (key: string) => string[] };
  const all = intl.supportedValuesOf?.("timeZone") ?? ["UTC", "America/New_York", "America/Chicago", "America/Denver", "America/Los_Angeles", "Europe/London", "Europe/Dublin", "Asia/Tokyo", "Australia/Sydney"];
  return all.includes("UTC") ? all : ["UTC", ...all];
}
